import React, { useMemo, useState } from 'react'
import { Table, Form } from 'react-bootstrap-v5'
import { PlanningTabProps, dateLabel, downloadCsv, matchesSearch, quantity, usePlanningReport } from '../helpers/PlanningHelper'
import { PlanningNotice, PlanningTiles, ProductName } from './PlanningParts'

type CoverType = 'products' | 'materials'

const flags = ['negative', 'out', 'critical', 'low', 'ok', 'idle']

const flagBadges: { [flag: string]: string } = {
	negative: 'badge-light-danger',
	out: 'badge-light-danger',
	critical: 'badge-light-warning',
	low: 'badge-light-primary',
	ok: 'badge-light-success',
	idle: 'badge-light-dark',
}

const PlanningStockCover: React.FC<PlanningTabProps> = ({ active, filters }) => {
	const [type, setType] = useState<CoverType>('products')
	const [days, setDays] = useState(90)
	const [flag, setFlag] = useState('')
	const [search, setSearch] = useState('')

	const { loading, data, errors } = usePlanningReport(
		active,
		'stock-cover',
		filters.idProjects ? { idProjects: filters.idProjects, asOfDate: filters.asOfDate, type, days } : null
	)

	// the answer on screen may still be the other list while a new one loads
	const isProducts = (data?.type ?? type) === 'products'
	const usedLabel = isProducts ? 'Delivered' : 'Used'

	const flagLabels: { [flag: string]: string } = {
		negative: 'Negative stock',
		out: 'Out of stock',
		critical: `Under ${data?.criticalDays ?? 15} days`,
		low: `${data?.criticalDays ?? 15} to ${data?.lowDays ?? 30} days`,
		ok: 'Enough',
		idle: 'No movement',
	}

	const rows = useMemo(() => {
		return (data?.rows ?? []).filter((row: any) =>
			(flag === '' || row.flag === flag) &&
			matchesSearch(search, row.productName, row.productCode, row.itemName, row.itemCode, row.categoryName)
		)
	}, [data, flag, search])

	const orderOverdue = (row: any) => row.orderBy && row.orderBy <= data.asOfDate

	const exportCsv = () => {
		if (isProducts) {
			downloadCsv(
				`stock-cover-finished-goods-${data.asOfDate}.csv`,
				['Product', 'Product Code', 'Category', 'Pack Size', 'Unit', 'Stock', 'Stock Volume', `Delivered in ${data.days} days`, 'Per Day', 'To Deliver', 'Cover Days', 'Runs Out On', 'Status'],
				rows.map((row: any) => [
					row.productName, row.productCode, row.categoryName, row.packSize, row.unitName, row.stock, row.stockVolume, row.used, row.perDay,
					row.pending, row.coverDays, row.runOutDate, flagLabels[row.flag],
				])
			)
			return
		}

		downloadCsv(
			`stock-cover-materials-${data.asOfDate}.csv`,
			['Material', 'Item Code', 'Category', 'Unit', 'Stock', `Used in ${data.days} days`, 'Per Day', 'Open PO', 'Cover Days', 'Runs Out On', 'Lead Days', 'Bought By', 'Order By', 'Status'],
			rows.map((row: any) => [
				row.itemName, row.itemCode, row.categoryName, row.unitName, row.stock, row.used, row.perDay, row.openPo, row.coverDays, row.runOutDate,
				row.leadDays, row.leadSource, row.orderBy, flagLabels[row.flag],
			])
		)
	}

	return (
		<>
			<p className='text-muted small mb-3'>
				Days of stock left if {isProducts ? 'deliveries' : 'use in production'} continue at the rate of the last {data?.days ?? days} days.
				{isProducts
					? ' "To deliver" is ordered in the last 90 days and not yet delivered.'
					: ' "Order by" is the run-out date less the usual wait between purchase order and receipt; open POs of the last 180 days are shown beside it.'}
			</p>

			<div className='row align-items-end'>
				<div className='col-md-3'>
					<Form.Group className='mb-3' controlId='coverType'>
						<Form.Label>Show</Form.Label>
						<Form.Select id='coverType' value={type} onChange={(event) => { setType(event.currentTarget.value as CoverType); setFlag('') }}>
							<option value='products'>Finished goods</option>
							<option value='materials'>Materials</option>
						</Form.Select>
					</Form.Group>
				</div>
				<div className='col-md-3'>
					<Form.Group className='mb-3' controlId='coverDays'>
						<Form.Label>Rate of the last</Form.Label>
						<Form.Select id='coverDays' value={days} onChange={(event) => setDays(parseInt(event.currentTarget.value, 10))}>
							<option value={30}>30 days</option>
							<option value={60}>60 days</option>
							<option value={90}>90 days</option>
							<option value={180}>180 days</option>
						</Form.Select>
					</Form.Group>
				</div>
				<div className='col-md-4'>
					<Form.Group className='mb-3' controlId='coverSearch'>
						<Form.Label>Search</Form.Label>
						<Form.Control type='text' placeholder='Name, code or category' value={search} onChange={(event) => setSearch(event.target.value)} />
					</Form.Group>
				</div>
				<div className='col-md-2'>
					<div className='mb-3'>
						<button className='btn btn-sm btn-outline-primary p-3' onClick={exportCsv} disabled={rows.length === 0}>
							<i className='fas fa-download me-1'></i>Excel (CSV)
						</button>
					</div>
				</div>
			</div>

			<PlanningNotice loading={loading} errors={errors} hasData={!!data} />

			{data && errors.length === 0 && (
				<>
					<PlanningTiles
						tiles={flags.map((key) => ({
							label: flagLabels[key],
							value: String(data.summary[key] ?? 0),
							hint: 'Click to show only these',
							active: flag === key,
							onClick: () => setFlag(flag === key ? '' : key),
						}))}
					/>

					<Table responsive striped bordered hover size='sm' className='mb-0 bg-white'>
						<thead className='table-light'>
							<tr>
								<th>SL</th>
								<th>{isProducts ? 'Product' : 'Material'}</th>
								<th>{isProducts ? 'Pack Size' : 'Category'}</th>
								<th>Unit</th>
								<th className='text-end'>Stock</th>
								{isProducts && <th className='text-end'>Stock Volume</th>}
								<th className='text-end'>{usedLabel} in {data.days} days</th>
								<th className='text-end'>Per Day</th>
								<th className='text-end'>{isProducts ? 'To Deliver' : 'Open PO'}</th>
								<th className='text-end'>Cover (days)</th>
								<th>Runs Out On</th>
								{!isProducts && <th>Usual Wait</th>}
								{!isProducts && <th>Order By</th>}
								<th>Status</th>
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && (
								<tr>
									<td colSpan={14} className='text-center text-muted py-4'>Nothing to show for these filters.</td>
								</tr>
							)}
							{rows.map((row: any, index: number) => (
								<tr key={isProducts ? row.idProducts : row.idItems}>
									<td>{index + 1}</td>
									<td>
										{isProducts
											? <ProductName row={row} />
											: <>{row.itemName}{row.itemCode ? <span className='text-muted'> ({row.itemCode})</span> : null}</>}
									</td>
									<td>{isProducts ? row.packSize : row.categoryName}</td>
									<td>{row.unitName}</td>
									<td className={`text-end ${row.stock < 0 ? 'text-danger fw-bold' : ''}`}>{quantity(row.stock, 2)}</td>
									{isProducts && <td className='text-end'>{quantity(row.stockVolume)} {row.itemUnitName}</td>}
									<td className='text-end'>{quantity(row.used, 2)}</td>
									<td className='text-end'>{row.perDay > 0 ? quantity(row.perDay, 2) : ''}</td>
									<td className='text-end'>{(isProducts ? row.pending : row.openPo) > 0 ? quantity(isProducts ? row.pending : row.openPo, 2) : ''}</td>
									<td className='text-end fw-bold'>{row.coverDays ?? ''}</td>
									<td style={{ whiteSpace: 'nowrap' }}>{row.coverDays === 0 ? 'Now' : dateLabel(row.runOutDate)}</td>
									{!isProducts && <td style={{ whiteSpace: 'nowrap' }}>{row.leadDays !== null ? `${row.leadDays} days (${row.leadSource})` : ''}</td>}
									{!isProducts && (
										<td style={{ whiteSpace: 'nowrap' }} className={orderOverdue(row) ? 'text-danger fw-bold' : ''} title={orderOverdue(row) ? 'This date has passed: order now' : ''}>
											{dateLabel(row.orderBy)}
										</td>
									)}
									<td><span className={`badge ${flagBadges[row.flag]}`}>{flagLabels[row.flag]}</span></td>
								</tr>
							))}
						</tbody>
					</Table>
				</>
			)}
		</>
	)
}

export default PlanningStockCover
